import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BuildIcon from '@mui/icons-material/Build';
import BusinessIcon from '@mui/icons-material/Business';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MotionPhotosAutoIcon from '@mui/icons-material/MotionPhotosAuto';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import api from '../../services/api/axiosConfig';
import { hostService } from '../../services/hostService';

interface Order {
  id: string;
  user_id: string;
  order_number: string;
  total_amount: number;
  payment_status: string;
  order_details: {
    partnerId: string;
    partnerName?: string;
    units: number;
    unitPrice: number;
    productId: string;
    version: string;
    email: string;
    phone: string;
  };
  shipping_address: any;
  billing_address: any;
  paid_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

interface OrdersResponse {
  success: boolean;
  totalItems: number;
  data: Order[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

const HostDashboardContent = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [timeFilter, setTimeFilter] = useState('7 Days');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const [formData, setFormData] = useState({
    partnerId: '',
    email: '',
    phone: '',
    units: 1,
    unitPrice: 0,
    productId: 'botel',
    version: 'v1.0.0',
    promoCode: ''
  });

  // Calculate base price per unit based on subscription plan
  const getBasePricePerUnit = (sub?: any) => {
    const subData = sub || subscription;
    if (!subData || !Array.isArray(subData) || subData.length === 0) return 0;
    const planName = subData[0]?.plan_name || '';
    switch (planName.toLowerCase()) {
      case 'premium':
        return 49;
      case 'basic':
        return 29;
      case 'enterprise':
        return 79;
      default:
        return 0;
    }
  };

  // Calculate total unit price based on units and plan
  const calculateUnitPrice = (units: number, sub?: any) => {
    const basePrice = getBasePricePerUnit(sub);
    return basePrice * units;
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await api.get<any>('/partners/my-orders');
        // Handle response - could be response.data.data or response.data depending on API structure
        let ordersArray: Order[] = [];
        if (response.data?.success && response.data?.data) {
          ordersArray = response.data.data;
        } else if (Array.isArray(response.data)) {
          ordersArray = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          ordersArray = response.data.data;
        }
        setOrders(ordersArray);
        // Get partnerId from first order if available
        if (ordersArray.length > 0 && ordersArray[0].order_details?.partnerId) {
          setFormData(prev => ({
            ...prev,
            partnerId: ordersArray[0].order_details.partnerId,
            email: user?.email || '',
            phone: ''
          }));
        }
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchSubscription = async () => {
      try {
        const subscriptionData = await hostService.getSubscription();
        // Handle different response structures - subscription should be an array
        const sub = Array.isArray(subscriptionData?.data) ? subscriptionData.data : 
                   Array.isArray(subscriptionData) ? subscriptionData : null;
        setSubscription(sub);
        
        // Update unit price when subscription is loaded
        if (sub && Array.isArray(sub) && sub.length > 0) {
          const basePrice = getBasePricePerUnit(sub);
          if (basePrice > 0) {
            setFormData(prev => ({
              ...prev,
              unitPrice: basePrice * prev.units
            }));
          } else {
            // If no valid plan, set unit price to 0
            setFormData(prev => ({
              ...prev,
              unitPrice: 0
            }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      }
    };

    fetchOrders();
    fetchSubscription();
  }, [user]);

  const handleOpenOrderDialog = () => {
    // Try to get partnerId from existing orders
    if (orders.length > 0 && orders[0].order_details?.partnerId) {
      const basePrice = getBasePricePerUnit(subscription);
      const calculatedPrice = basePrice > 0 ? basePrice * formData.units : 0;
      setFormData(prev => ({
        ...prev,
        partnerId: orders[0].order_details.partnerId,
        email: user?.email || prev.email,
        unitPrice: calculatedPrice
      }));
    } else {
      // Update unit price even if no orders
      const basePrice = getBasePricePerUnit(subscription);
      const calculatedPrice = basePrice > 0 ? basePrice * formData.units : 0;
      setFormData(prev => ({
        ...prev,
        unitPrice: calculatedPrice
      }));
    }
    setOrderDialogOpen(true);
  };

  const handleCloseOrderDialog = () => {
    setOrderDialogOpen(false);
    // Reset form with calculated unit price
    const basePrice = getBasePricePerUnit(subscription);
    const calculatedPrice = basePrice > 0 ? basePrice * 1 : 0;
    setFormData({
      partnerId: orders.length > 0 && orders[0].order_details?.partnerId ? orders[0].order_details.partnerId : '',
      email: user?.email || '',
      phone: '',
      units: 1,
      unitPrice: calculatedPrice,
      productId: 'botel',
      version: 'v1.0.0',
      promoCode: ''
    });
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If units changed, recalculate unit price
      if (field === 'units') {
        const newUnits = typeof value === 'number' ? value : parseInt(value as string) || 1;
        updated.unitPrice = calculateUnitPrice(newUnits, subscription);
      }
      
      return updated;
    });
  };

  const handleSubmitOrder = async () => {
    try {
      setSubmitting(true);
      const payload = {
        partnerId: formData.partnerId,
        email: user?.email || '',
        phone: formData.phone,
        units: formData.units,
        unitPrice: formData.unitPrice,
        productId: 'botel',
        version: 'v1.0.0',
        promoCode: formData.promoCode || null
      };

      const response = await api.post('/partners/order', payload);
      
      if (response.data) {
        // Show success toast
        showNotification('Order placed successfully!', 'success');
        
        // Close dialog
        handleCloseOrderDialog();
        
        // Refresh orders list
        try {
          const ordersResponse = await api.get<any>('/partners/my-orders');
          let ordersArray: Order[] = [];
          if (ordersResponse.data?.success && ordersResponse.data?.data) {
            ordersArray = ordersResponse.data.data;
          } else if (Array.isArray(ordersResponse.data)) {
            ordersArray = ordersResponse.data;
          } else if (ordersResponse.data?.data && Array.isArray(ordersResponse.data.data)) {
            ordersArray = ordersResponse.data.data;
          }
          setOrders(ordersArray);
        } catch (fetchError) {
          console.error('Failed to refresh orders:', fetchError);
        }
      }
    } catch (error: any) {
      console.error('Failed to place order:', error);
      showNotification(error.response?.data?.message || 'Failed to place order. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Mock data matching the image
  const stats = {
    monthlyEarnings: {
      amount: '$1500.00',
      change: '12% From Last Month',
      isPositive: true
    },
    occupancyRate: {
      rate: '85%',
      refunds: '2 Refunds Processing'
    },
    securityAlerts: {
      count: '2 Active',
      status: 'Requires Attention'
    },
    nextPayment: {
      date: 'June 15, 2025',
      amount: '$53.50 Scheduled'
    }
  };

  const securityAlerts = [
    {
      id: 1,
      type: 'Unauthorized Entry Attempt',
      location: 'Botel One Hawthorne Atlanta',
      time: '2 Hours Ago',
      severity: 'high'
    },
    {
      id: 2,
      type: 'Motion Detected',
      location: 'Botel One Hawthorne Atlanta',
      time: '20 Mins Ago',
      severity: 'low'
    }
  ];


  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: { xs: 2, md: 3 }, 
      background: '#151A23', 
      minHeight: '100vh', 
      color: '#fff',
      marginTop: 8
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <BusinessIcon sx={{ fontSize: 32, color: '#4A90E2' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff' }}>
              Welcome Back, {user?.name || 'Troy'}!
            </Typography>
            <Typography variant="body1" sx={{ color: '#b0b8c1' }}>
              Here's What's Happening With Your Botels Today.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: '#232B3E', borderRadius: 3, height: '100%', position: 'relative' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AttachMoneyIcon sx={{ color: '#4CAF50', mr: 1 }} />
                <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                  Monthly Earnings
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff', mb: 1 }}>
                {stats.monthlyEarnings.amount}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ color: '#4CAF50', fontSize: 16, mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                  {stats.monthlyEarnings.change}
                </Typography>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backdropFilter: 'blur(2px)',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  color: '#9ca3af',
                  fontSize: 24,
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                Coming Soon
              </Typography>
              <Typography
                sx={{
                  color: '#6b7280',
                  fontSize: 16,
                  textAlign: 'center',
                  px: 2,
                }}
              >
                This feature will be available in a future release
              </Typography>
            </Box>
          </Card>
        </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: '#232B3E', borderRadius: 3, height: '100%', position: 'relative' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EventAvailableIcon sx={{ color: '#2196F3', mr: 1 }} />
                <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                  Occupancy Rate
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff', mb: 1 }}>
                {stats.occupancyRate.rate}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                {stats.occupancyRate.refunds}
              </Typography>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backdropFilter: 'blur(2px)',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  color: '#9ca3af',
                  fontSize: 24,
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                Coming Soon
              </Typography>
              <Typography
                sx={{
                  color: '#6b7280',
                  fontSize: 16,
                  textAlign: 'center',
                  px: 2,
                }}
              >
                This feature will be available in a future release
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: '#232B3E', borderRadius: 3, height: '100%', position: 'relative' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ color: '#FF9800', mr: 1 }} />
                <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                  Security Alerts
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff', mb: 1 }}>
                {stats.securityAlerts.count}
              </Typography>
              <Typography variant="body2" sx={{ color: '#FF9800' }}>
                {stats.securityAlerts.status}
              </Typography>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backdropFilter: 'blur(2px)',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  color: '#9ca3af',
                  fontSize: 24,
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                Coming Soon
              </Typography>
              <Typography
                sx={{
                  color: '#6b7280',
                  fontSize: 16,
                  textAlign: 'center',
                  px: 2,
                }}
              >
                This feature will be available in a future release
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: '#232B3E', borderRadius: 3, height: '100%', position: 'relative' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon sx={{ color: '#9C27B0', mr: 1 }} />
                <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                  Next Payment
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff', mb: 1 }}>
                {stats.nextPayment.date}
              </Typography>
              <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                {stats.nextPayment.amount}
              </Typography>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backdropFilter: 'blur(2px)',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  color: '#9ca3af',
                  fontSize: 24,
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                Coming Soon
              </Typography>
              <Typography
                sx={{
                  color: '#6b7280',
                  fontSize: 16,
                  textAlign: 'center',
                  px: 2,
                }}
              >
                This feature will be available in a future release
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button
            fullWidth
            variant="contained"
            disabled
            startIcon={<SettingsIcon />}
            sx={{
              py: 2,
              background: '#2196F3',
              '&:hover': { background: '#1976D2' },
              borderRadius: 2,
              fontSize: 16
            }}
          >
            Modify Subscription
          </Button>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<LocalShippingIcon />}
            onClick={handleOpenOrderDialog}
            sx={{
              py: 2,
              background: '#9C27B0',
              '&:hover': { background: '#7B1FA2' },
              borderRadius: 2,
              fontSize: 16
            }}
          >
            Order More Botels
          </Button>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button
            fullWidth
            variant="contained"
            disabled
            startIcon={<BuildIcon />}
            sx={{
              py: 2,
              background: '#F44336',
              '&:hover': { background: '#D32F2F' },
              borderRadius: 2,
              fontSize: 16
            }}
          >
            Request Maintenance
          </Button>
        </Grid>
      </Grid>

      {/* Earnings Overview */}
      <Paper sx={{ p: 3, mb: 4, background: '#232B3E', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          {/* <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>
            Earnings Overview
          </Typography> */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              sx={{
                color: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4A5568',
                },
                '& .MuiSvgIcon-root': {
                  color: '#fff',
                },
              }}
              IconComponent={ExpandMoreIcon}
            >
              <MenuItem value="7 Days">7 Days</MenuItem>
              <MenuItem value="30 Days">30 Days</MenuItem>
              <MenuItem value="3 Months">3 Months</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#fff' }}>
          My Orders
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                  Order Number
                </TableCell>
                <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                  Units
                </TableCell>
                <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                  Total Amount
                </TableCell>
                <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                  Payment Status
                </TableCell>
                <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}>
                  Created At
                </TableCell>
                <TableCell sx={{ color: '#b0b8c1', borderColor: '#4A5568' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: '#fff', borderColor: '#4A5568', textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                      Loading orders...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: '#fff', borderColor: '#4A5568', textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                      No orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {order.order_number}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#b0b8c1', fontSize: '0.75rem' }}>
                          {order.order_details?.partnerName || order.order_details?.email || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                      {order.order_details?.units || 0}
                    </TableCell>
                    <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                      ${(order.total_amount * 100).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                      <Box
                        sx={{
                          background: order.payment_status === 'paid' ? '#4CAF50' : 
                                     order.payment_status === 'pending' ? '#FF9800' : '#F44336',
                          color: '#fff',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: 12,
                          display: 'inline-block',
                          textTransform: 'capitalize'
                        }}
                      >
                        {order.payment_status}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                      <Typography variant="body2">
                        {new Date(order.created_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#b0b8c1', fontSize: '0.75rem' }}>
                        {new Date(order.created_at).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', borderColor: '#4A5568' }}>
                      <Tooltip title="This feature will be available in future release">
                        <span>
                          <IconButton 
                            size="small" 
                            disabled
                            sx={{ color: '#b0b8c1' }}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recent Security Alerts */}
      <Paper sx={{ p: 3, mb: 4, background: '#232B3E', borderRadius: 3, position: 'relative' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, color: '#fff' }}>
          Recent Security Alerts
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {securityAlerts.map((alert) => (
            <Paper
              key={alert.id}
              sx={{
                p: 3,
                background: alert.severity === 'high' ? '#2D1B1B' : '#1B2D1B',
                border: alert.severity === 'high' ? '1px solid #FF5252' : '1px solid #4CAF50',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {alert.severity === 'high' ? (
                  <WarningIcon sx={{ color: '#FF5252', fontSize: 28 }} />
                ) : (
                  <MotionPhotosAutoIcon sx={{ color: '#4CAF50', fontSize: 28 }} />
                )}
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>
                    {alert.type}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                    {alert.location}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                {alert.time}
              </Typography>
            </Paper>
          ))}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            backdropFilter: 'blur(2px)',
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: 24,
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: 16,
              textAlign: 'center',
              px: 2,
            }}
          >
            This feature will be available in a future release
          </Typography>
        </Box>
      </Paper>

      {/* Order More Botels Dialog */}
      <Dialog 
        open={orderDialogOpen} 
        onClose={handleCloseOrderDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#232B3E',
            color: '#fff'
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #4A5568', pb: 2 }}>
          Order More Botels
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt:"20px" }}>
            <TextField
              label="Partner ID"
              value={formData.partnerId}
              disabled
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: '#4A5568',
                  },
                  '&.Mui-disabled': {
                    color: '#b0b8c1',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#b0b8c1',
                }
              }}
            />
            <TextField
              label="Phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              required
              fullWidth
              placeholder="+1234567890"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: '#4A5568',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#b0b8c1',
                }
              }}
            />
            <TextField
              label="Units"
              type="number"
              value={formData.units}
              onChange={(e) => handleInputChange('units', parseInt(e.target.value) || 1)}
              required
              fullWidth
              inputProps={{ min: 1 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: '#4A5568',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#b0b8c1',
                }
              }}
            />
            <TextField
              label="Unit Price"
              type="number"
              value={formData.unitPrice || ''}
              disabled
              required
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              helperText={subscription && Array.isArray(subscription) && subscription.length > 0 ? `Based on ${subscription[0].plan_name || 'N/A'} plan (${getBasePricePerUnit() || 0} per unit × ${formData.units} units)` : 'No subscription found'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: '#4A5568',
                  },
                  '&.Mui-disabled': {
                    color: '#b0b8c1',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#b0b8c1',
                },
                '& .MuiFormHelperText-root': {
                  color: '#6b7280',
                }
              }}
            />
            <TextField
              label="Promo Code (Optional)"
              value={formData.promoCode}
              onChange={(e) => handleInputChange('promoCode', e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: '#4A5568',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#b0b8c1',
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #4A5568' }}>
          <Button
            onClick={handleCloseOrderDialog}
            sx={{
              color: '#b0b8c1',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitOrder}
            disabled={submitting || !formData.partnerId || !user?.email || !formData.phone}
            variant="contained"
            sx={{
              background: '#9C27B0',
              '&:hover': { background: '#7B1FA2' },
              '&:disabled': {
                background: '#4A5568',
                color: '#6b7280'
              }
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HostDashboardContent;
